import { useState } from "preact/hooks"
import { Button } from "../../../../components/ui/button"
import { validateCalendar } from '../validation'
import { PromotionFormField, SearchableSelectField } from './form_fields'
import { getYear } from 'date-fns'
import { CalendarInfo } from '../types'
import { customer_select_options, merge_account_names, use_promotion_account_options } from "../account_options"

export interface CalendarFormData {
  name: string
  description?: string
  year?: number
  retailer_name?: string
  brand_name?: string
  /** The customer (distributor, or "Direct") the calendar's promotions bill through. */
  customer_name?: string
}

interface CalendarFormProps {
  heading: string
  initial: { name: string; description: string; year?: number; retailer_name: string; brand_name: string; customer_name: string }
  saveLabel: string
  savingLabel: string
  onSave: (calendarData: CalendarFormData) => void
  onCancel: () => void
  isSaving: boolean
}

/** Shared field layout for creating and editing a promotional calendar. */
function CalendarForm({ heading, initial, saveLabel, savingLabel, onSave, onCancel, isSaving }: CalendarFormProps) {
  const [calendarData, setCalendarData] = useState(initial)
  const account_options = use_promotion_account_options()
  const customer_names = merge_account_names([calendarData.customer_name], account_options?.customers)

  function handleSave() {
    if (validateCalendar(calendarData)) {
      onSave({
        name: calendarData.name,
        description: calendarData.description || undefined,
        year: calendarData.year || undefined,
        retailer_name: calendarData.retailer_name || undefined,
        brand_name: calendarData.brand_name || undefined,
        customer_name: calendarData.customer_name || undefined,
      })
    }
  }

  return (
    <div class="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <h5 class="text-sm font-medium text-gray-700 mb-3">{heading}</h5>
      <div class="space-y-3">
        <PromotionFormField
          label="Calendar Name"
          value={calendarData.name}
          onChange={(value) => setCalendarData({ ...calendarData, name: value as string })}
          placeholder="e.g., 2026 Promotional Calendar"
          required
        />

        <PromotionFormField
          label="Description"
          value={calendarData.description}
          onChange={(value) => setCalendarData({ ...calendarData, description: value as string })}
          placeholder="Optional description of this calendar"
        />

        <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
          <SearchableSelectField
            label="Customer (Distributor)"
            value={calendarData.customer_name}
            onChange={(value: string) => setCalendarData({ ...calendarData, customer_name: value })}
            onCreateOption={(value: string) => setCalendarData({ ...calendarData, customer_name: value })}
            createOptionLabel={(text: string) => `Add "${text}" as a new customer`}
            options={[{ value: "", label: "No customer yet" }, ...customer_select_options(customer_names)]}
            placeholder="Select a customer"
            searchPlaceholder="Search customers, or type a new one..."
            emptyMessage="No matching customers. Type a name to add it."
            help="Who the calendar's promotions bill through. Copied onto each new promotion, so it is not typed again."
          />
          <PromotionFormField
            label="Retailer"
            value={calendarData.retailer_name}
            onChange={(value) => setCalendarData({ ...calendarData, retailer_name: value as string })}
            placeholder="e.g., Safeway"
            help="Where shoppers buy, when the calendar is for one retailer."
          />
        </div>
        <div class="grid grid-cols-2 gap-3">
          <PromotionFormField
            label="Year"
            value={calendarData.year}
            onChange={(value) => setCalendarData({ ...calendarData, year: value as number })}
            type="number"
            placeholder="2025"
          />
          <PromotionFormField
            label="Brand"
            value={calendarData.brand_name}
            onChange={(value) => setCalendarData({ ...calendarData, brand_name: value as string })}
            placeholder="Brand name"
          />
        </div>

        <div class="flex gap-2">
          <Button onClick={handleSave} disabled={isSaving} class="flex-1">
            {isSaving ? savingLabel : saveLabel}
          </Button>
          <Button onClick={onCancel} variant="outline" disabled={isSaving}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  )
}

interface CreateCalendarFormProps {
  onSave: (calendarData: CalendarFormData) => void
  onCancel: () => void
  isSaving: boolean
}

export function CreateCalendarForm({ onSave, onCancel, isSaving }: CreateCalendarFormProps) {
  return (
    <CalendarForm
      heading="Create New Promotional Calendar"
      initial={{
        name: "",
        description: "",
        year: getYear(new Date()),
        retailer_name: "",
        brand_name: "",
        customer_name: "",
      }}
      saveLabel="Create Calendar"
      savingLabel="Creating..."
      onSave={onSave}
      onCancel={onCancel}
      isSaving={isSaving}
    />
  )
}

interface EditCalendarFormProps {
  calendar: CalendarInfo
  onSave: (calendarData: CalendarFormData) => void
  onCancel: () => void
  isSaving: boolean
}

/** Edit the calendar itself — name, description, year, retailer, brand — not its promotions. */
export function EditCalendarForm({ calendar, onSave, onCancel, isSaving }: EditCalendarFormProps) {
  return (
    <CalendarForm
      heading="Edit Calendar"
      initial={{
        name: calendar.calendar_name,
        description: calendar.calendar_description ?? "",
        year: calendar.calendar_year,
        retailer_name: calendar.retailer_name ?? "",
        brand_name: calendar.brand_name ?? "",
        customer_name: calendar.customer_name ?? "",
      }}
      saveLabel="Save Changes"
      savingLabel="Saving..."
      onSave={onSave}
      onCancel={onCancel}
      isSaving={isSaving}
    />
  )
}
